import api, { deleteWorkspaceUpload, uploadWorkspaceFile } from './apiService';
import type { MessageSourcesUsed } from './apiService';

/** Whether the signed-in account is on the free plan. Set by App; Lookup
 *  calls go to chat-free when it is and the service is configured. */
let lookupUsesChatFree = false;
export function setLookupUsesChatFree(active: boolean): void {
  lookupUsesChatFree = active;
}
export function isLookupChatFree(): boolean {
  return lookupUsesChatFree && !!api.getChatFreeServiceUrl();
}
/** The service every Lookup call goes to: chat-free for a free-plan account. */
function lookupBase(fallback: string): string {
  return isLookupChatFree() ? api.getChatFreeServiceUrl() : fallback;
}

/** A file attached to a user message: a report-library upload in the chat's
 *  own workspace (`ws_chat_<chat id>`). Saved with the chat, so a reopened
 *  chat still shows it and the model still sees its note. */
export interface LookupAttachment {
  upload_id: string;
  name: string;
}

export interface LookupMessage {
  role: 'user' | 'assistant';
  content: string;
  attachments?: LookupAttachment[];
}

/** Uploads files into the chat's workspace through Research's upload route;
 *  report-library analyses and indexes them as it does a project's. All or
 *  nothing: on a failure the files already uploaded are removed again. */
export async function uploadChatFiles(
  chatId: string,
  files: File[],
  userId: string,
  multipartMaxBytes?: number,
): Promise<LookupAttachment[]> {
  const workspaceId = `ws_chat_${chatId}`;
  const done: LookupAttachment[] = [];
  try {
    for (const file of files) {
      const result = await uploadWorkspaceFile(workspaceId, userId, '', file, file.name, undefined,
        multipartMaxBytes ? { multipart_max_bytes: multipartMaxBytes } : undefined);
      if (!result.success || !result.uploaded_file) throw new Error(result.error || `${file.name} could not be uploaded.`);
      done.push({ upload_id: result.uploaded_file.upload_id, name: file.name });
    }
  } catch (cause) {
    await Promise.allSettled(done.map(a => deleteWorkspaceUpload(workspaceId, userId, a.upload_id)));
    throw cause instanceof Error ? cause : new Error('The file could not be uploaded.');
  }
  return done;
}

export interface ConceptTerm {
  term: string;
  url: string;
}

export interface DefinitionPreview {
  text: string;
  truncated: boolean;
  locator: string;
  url: string;
  notice?: string;
  concept_terms?: ConceptTerm[];
}

/** `chatId`: the conversation the preview was opened in; its analytics rows
 *  group under it. */
export async function getDefinitionPreview(url: string, token: string, signal: AbortSignal, chatId?: string): Promise<DefinitionPreview> {
  await api.initialize();
  const response = await fetch(`${lookupBase(api.getWorkflowOrchestratorUrl())}/api/lookup/definition`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(chatId ? { url, chat_id: chatId } : { url }),
    signal,
  });
  if (!response.ok) throw new Error('Preview unavailable. You can still open the definition in EUR-Lex.');
  const preview = await response.json();
  if (typeof preview.text !== 'string' || !preview.text || typeof preview.truncated !== 'boolean'
      || typeof preview.locator !== 'string' || preview.url !== url
      || (preview.notice !== undefined && typeof preview.notice !== 'string')
      || (preview.concept_terms !== undefined && (!Array.isArray(preview.concept_terms)
          || !preview.concept_terms.every((term: ConceptTerm) => term && typeof term.term === 'string' && typeof term.url === 'string')))) {
    throw new Error('Preview unavailable. You can still open the definition in EUR-Lex.');
  }
  return preview;
}

/** One headline of a layered answer and the part of the raw answer it opens:
 *  `text.slice(start, end)` (UTF-16 offsets, end exclusive). */
export interface LayeredAnswerBlock {
  headline: string;
  start: number;
  end: number;
}

/** How a finished answer splits into headlines with "Read more" bodies.
 *  Absent when the answer was not layered; the answer then shows as written. */
export interface LayeredAnswerLayout {
  blocks: LayeredAnswerBlock[];
  /** Which block is the References block (the last one), or null. */
  references: number | null;
}

export type LookupEvent =
  | { event: 'token' | 'thinking'; data: { text: string; sources?: MessageSourcesUsed } }
  /** `turn_id` names the turn for analytics; `recordReadMore` and `recordLink` send it back. */
  | { event: 'done'; data: { text: string; sources?: MessageSourcesUsed; concepts?: ConceptTerm[]; layered?: LayeredAnswerLayout; turn_id?: string } }
  | { event: 'tool_call'; data: { id: string; name: string; arguments: Record<string, unknown> } }
  | { event: 'tool_result'; data: { id: string; name: string; ok: boolean; summary: string } };

export interface SavedLookupChat {
  chat_id: string;
  title: string;
  updated_at: string;
  /** In the list's Pinned group. */
  pinned?: boolean;
}

/** Saved chats live in report-library: `/lookup-chats` (docs/specs/lookup.md). */
async function lookupChats(path: string, token: string, init: RequestInit = {}): Promise<Response> {
  await api.initialize();
  const response = await fetch(`${lookupBase(api.getReportLibraryUrl())}/lookup-chats${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok && response.status !== 404) throw new Error('Saved chats are unavailable.');
  return response;
}

export async function listLookupChats(token: string): Promise<SavedLookupChat[]> {
  return (await (await lookupChats('', token)).json()).chats;
}

/** The saved thread, or null when the chat was never saved. */
export async function getLookupChat<T>(chatId: string, token: string): Promise<T[] | null> {
  const response = await lookupChats(`/${encodeURIComponent(chatId)}`, token);
  return response.status === 404 ? null : (await response.json()).messages;
}

export async function saveLookupChat(chatId: string, messages: unknown[], token: string): Promise<void> {
  const response = await lookupChats(`/${encodeURIComponent(chatId)}`, token, { method: 'PUT', body: JSON.stringify({ messages }) });
  if (!response.ok) throw new Error('Chat could not be saved.');
}

export async function pinLookupChat(chatId: string, pinned: boolean, token: string): Promise<void> {
  const response = await lookupChats(`/${encodeURIComponent(chatId)}`, token, { method: 'PATCH', body: JSON.stringify({ pinned }) });
  if (!response.ok) throw new Error('Chat could not be pinned.');
}

/** Deletes a chat for good. A chat already gone (404) counts as deleted. */
export async function deleteLookupChat(chatId: string, token: string): Promise<void> {
  await lookupChats(`/${encodeURIComponent(chatId)}`, token, { method: 'DELETE' });
}

export async function renameLookupChat(chatId: string, title: string, token: string): Promise<void> {
  const response = await lookupChats(`/${encodeURIComponent(chatId)}`, token, { method: 'PATCH', body: JSON.stringify({ title }) });
  if (!response.ok) throw new Error('Chat could not be renamed.');
}

/** One analytics row. Fire and forget: it never throws, and a failure is not
 *  shown, because it changes nothing in the chat. `keepalive`, and no config
 *  round trip once the service URL is known, so a send from `pagehide` still
 *  leaves as the page goes. */
async function recordLookupEvent(path: string, body: Record<string, unknown>, token: string): Promise<void> {
  try {
    if (!api.getWorkflowOrchestratorUrl()) await api.initialize();
    await fetch(`${lookupBase(api.getWorkflowOrchestratorUrl())}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Analytics only: the chat carries on as if it had been recorded.
  }
}

/** On a close: whole seconds the block was open while the page was visible,
 *  and `left` when the user left with it open rather than closing it. */
export interface ReadMoreTiming {
  seconds?: number;
  left?: boolean;
}

/** Tells analytics that the user opened (`open`) or closed one block of a
 *  layered answer; a close may carry its `timing`. Fire and forget. */
export async function recordReadMore(turnId: string, block: number, open: boolean, token: string, timing: ReadMoreTiming = {}): Promise<void> {
  const body: Record<string, unknown> = { turn_id: turnId, block, open };
  if (timing.seconds !== undefined) body.seconds = timing.seconds;
  if (timing.left) body.left = true;
  await recordLookupEvent('/api/lookup/read-more', body, token);
}

/** One block an "Expand all" or "Collapse all" press opened or closed; a
 *  close carries the whole seconds it was open while the page was visible. */
export interface ExpandAllBlock {
  block: number;
  seconds?: number;
}

/** Tells analytics that the user pressed "Expand all" (`open`) or "Collapse
 *  all" on a layered answer: one record for the press, with the blocks it
 *  opened or closed. Fire and forget. */
export async function recordExpandAll(turnId: string, open: boolean, blocks: ExpandAllBlock[], token: string): Promise<void> {
  await recordLookupEvent('/api/lookup/expand-all', { turn_id: turnId, open, blocks }, token);
}

/** Tells analytics that the user clicked a link in an answer or, with
 *  `awaySeconds`, came back to the Chat after that click: the whole seconds
 *  it was hidden. `block` is the block whose text held the link; null when
 *  the answer was shown whole. Fire and forget. */
export async function recordLink(turnId: string, block: number | null, url: string, token: string, awaySeconds?: number): Promise<void> {
  const body: Record<string, unknown> = { turn_id: turnId, block, url };
  if (awaySeconds !== undefined) body.away_seconds = awaySeconds;
  await recordLookupEvent('/api/lookup/link', body, token);
}

/** Read SSE over an authenticated POST, preserving frames across network chunks.
 *  `chatId` is the conversation's id: the turn's tool-call analytics rows
 *  group under it. */
export async function streamLookupTurn(
  messages: LookupMessage[],
  token: string,
  signal: AbortSignal,
  onEvent: (event: LookupEvent) => void,
  chatId?: string,
): Promise<void> {
  await api.initialize();
  const response = await fetch(`${lookupBase(api.getWorkflowOrchestratorUrl())}/api/lookup/turn`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(chatId ? { messages, chat_id: chatId } : { messages }),
    signal,
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error('Your session expired. Sign in again to continue.');
    // chat-free answers refusals as `{detail: {code, message}}`: show its message.
    let detail: any;
    try { detail = (await response.json())?.detail; } catch { detail = undefined; }
    if (detail && typeof detail.code === 'string' && typeof detail.message === 'string' && detail.message) {
      throw new Error(detail.message);
    }
    if (response.status === 429) throw new Error('Too many messages. Wait a moment and try again.');
    throw new Error('Chat is unavailable. Please try again.');
  }
  if (!response.body) throw new Error('Chat could not open the answer stream. Please try again.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  const dispatch = (frame: string) => {
    const lines = frame.split(/\r?\n/);
    const event = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
    const json = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
    if (!event || !json) return; // SSE keepalive/comment
    const data = JSON.parse(json);
    if (event === 'error') throw new Error(data.message || 'Chat could not complete this answer.');
    if (event === 'token' || event === 'thinking' || event === 'done') {
      if (typeof data.text !== 'string') throw new Error('Chat returned an invalid answer. Please try again.');
      onEvent({ event, data });
      if (event === 'done') finished = true;
    } else if (event === 'tool_call' || event === 'tool_result') {
      onEvent({ event, data });
    }
  };
  try {
    while (!finished) {
      const { done, value } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let boundary: RegExpExecArray | null;
      while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        dispatch(frame);
        if (finished) break;
      }
      if (done) {
        if (!finished && buffer.trim()) dispatch(buffer);
        break;
      }
    }
    if (!finished) throw new Error('The connection ended before the answer finished. Please try again.');
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
